if (!window.Eurus.loadedScript.has('color-swatches.js')) {
  window.Eurus.loadedScript.add('color-swatches.js');

  // One fetch per sibling handle per page; resolves to null when the product is missing/unpublished.
  const productCache = new Map();
  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(`${window.Shopify?.routes?.root || '/'}products/${handle}.js`)
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null));
    }
    return productCache.get(handle);
  };

  // Fallback when `base-<tag>` 404s because the handle spells the colour differently (tag "lightbrown", handle
  // "light-brown"). One predictive-search request per base handle; resolves to product-like objects.
  const searchCache = new Map();
  const searchSiblings = (base) => {
    if (!searchCache.has(base)) {
      const q = encodeURIComponent(base.replace(/-/g, ' '));
      searchCache.set(base, fetch(`${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${q}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`)
        .then((res) => (res.ok ? res.json() : null))
        .then((json) => (json?.resources?.results?.products || []).filter((p) => p.handle.startsWith(`${base}-`)))
        .catch(() => []));
    }
    return searchCache.get(base);
  };

  const sized = (url, width) => {
    if (!url) return '';
    const u = new URL(url, window.location.origin);
    u.searchParams.set('width', width);
    return u.toString();
  };

  class ColorSwatches extends HTMLElement {
    connectedCallback() {
      if (this.rendered) return;
      this.rendered = true;
      let swatches = [];
      try { swatches = JSON.parse(this.dataset.swatches || '[]'); } catch (e) { return; }
      this.load(swatches);
    }

    async load(swatches) {
      const products = await Promise.all(swatches.map((s) => (s.own ? Promise.resolve(null) : fetchProduct(s.handle))));
      const items = [];
      let searched = null;
      for (let i = 0; i < swatches.length; i++) {
        const s = swatches[i];
        if (s.own) {
          items.push({ ...s, url: this.dataset.ownUrl, image: this.dataset.ownImage });
        } else if (products[i] && products[i].featured_image) {
          items.push({ ...s, url: products[i].url, image: sized(products[i].featured_image, 96), full: products[i].featured_image });
        } else if (this.dataset.base) {
          searched = searched || (await searchSiblings(this.dataset.base));
          const match = searched.find((p) => p.handle.slice(this.dataset.base.length + 1).replace(/-/g, '') === s.key);
          const img = match?.featured_image?.url || match?.image;
          if (match && img) items.push({ ...s, url: match.url.split('?')[0], image: sized(img, 96), full: img });
        }
      }
      // A lone product still shows its own swatch (and keeps the theme's Colour option hidden on the PDP).
      if (!items.length) return;
      this.render(items);
    }

    render(items) {
      const card = this.closest('.card-product');
      const cardImg = card?.querySelector('img.preview-img');
      const original = cardImg ? { src: cardImg.getAttribute('src'), srcset: cardImg.getAttribute('srcset'), sizes: cardImg.getAttribute('sizes') } : null;

      const restore = () => {
        if (!cardImg || !original) return;
        original.srcset ? cardImg.setAttribute('srcset', original.srcset) : cardImg.removeAttribute('srcset');
        cardImg.setAttribute('src', original.src);
      };
      const preview = (item) => {
        if (!cardImg || !item.full) return;
        cardImg.removeAttribute('srcset');
        cardImg.setAttribute('src', sized(item.full, 900));
      };

      const isPdp = this.dataset.mode === 'pdp';
      const showName = this.hasAttribute('data-show-name');
      // Follow the card's own text alignment (center/right/left).
      const align = getComputedStyle(this.parentElement).textAlign;
      this.style.justifyContent = align === 'center' ? 'center' : (align === 'right' || align === 'end') ? 'flex-end' : 'flex-start';
      const MAX_VISIBLE = isPdp ? items.length : 3;
      // Own colour always leads, so it is never hidden behind "+n".
      items.sort((x, y) => Number(y.own) - Number(x.own));

      const build = (item) => {
        const a = document.createElement('a');
        a.href = item.url;
        a.title = item.color;
        a.setAttribute('aria-label', item.color);
        a.className = `card-color-swatch-link${item.own ? ' is-active' : ''}`;
        if (item.own) a.setAttribute('aria-current', 'true');

        const box = document.createElement('span');
        box.className = 'card-color-swatch';
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.color;
        img.loading = 'lazy';
        img.width = isPdp ? 56 : 36;
        img.height = isPdp ? 56 : 36;
        box.appendChild(img);
        a.appendChild(box);

        if (showName) {
          const name = document.createElement('span');
          name.className = 'card-color-swatch-name';
          name.textContent = item.color;
          a.appendChild(name);
        }

        if (!item.own && !isPdp) {
          a.addEventListener('mouseenter', () => preview(item));
          a.addEventListener('focus', () => preview(item));
          a.addEventListener('mouseleave', restore);
          a.addEventListener('blur', restore);
        }
        return a;
      };

      // Built off-DOM and swapped in at once, replacing the server-rendered own swatch without a flicker.
      const frag = document.createDocumentFragment();
      items.slice(0, MAX_VISIBLE).forEach((item) => frag.appendChild(build(item)));

      const extra = items.slice(MAX_VISIBLE);
      if (extra.length) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'card-color-swatch-more';
        more.textContent = `+${extra.length}`;
        more.setAttribute('aria-label', `Show ${extra.length} more colours`);
        more.addEventListener('click', (e) => {
          e.preventDefault();
          extra.forEach((item) => this.insertBefore(build(item), more));
          more.remove();
        });
        frag.appendChild(more);
      }
      this.replaceChildren(frag);
    }
  }

  if (!customElements.get('color-swatches')) customElements.define('color-swatches', ColorSwatches);
}
